/* Staff: sheet, spacing, quality and pricing settings, plus recent orders.
   Nothing in the editor hard-codes these: they all come from here. */

import { useEffect, useState } from 'react';
import { formatMoney, RATE_UNITS, type BuilderSettings, type PricingConfig } from '@gsb/core';
import { api, type StorageReport } from '../api';
import { EmailAdmin } from './AdminEmail';
import { Icon } from '../ui/icons';
import { Switch, Seg } from '../ui/primitives';
import { toast } from '../store/store';

/** Where to go after signing in: only a job page on this site (?next=/staff/s/<id>). */
function nextPage(): string | null {
  try {
    const n = new URLSearchParams(location.search).get('next') || '';
    return /^\/staff\/s\/[0-9a-f-]{36}$/i.test(n) ? n : null;
  } catch { return null; }
}

export function Admin() {
  const [me, setMe] = useState<boolean | null>(null);
  const next = nextPage();
  useEffect(() => { api.adminMe().then(r => { if (r.admin && next) location.replace(next); else setMe(r.admin); }).catch(() => setMe(false)); }, []);
  if (me === null) return null;
  return <main className="mine"><div className="mine-in">{me ? <Settings onOut={() => setMe(false)} /> : <Login onIn={() => { if (next) location.replace(next); else setMe(true); }} forJob={!!next} />}</div></main>;
}

function Login({ onIn, forJob }: { onIn: () => void; forJob?: boolean }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  return (
    <form className="card" style={{ maxWidth: 420, padding: 24, margin: '40px auto' }} onSubmit={async e => {
      e.preventDefault();
      try { await api.adminLogin(pw); onIn(); } catch (x) { setErr((x as Error).message); }
    }}>
      <span className="kicker">Staff</span>
      <h1 className="h2" style={{ margin: '6px 0 14px' }}>{forJob ? 'Sign in to open the job' : 'Builder settings'}</h1>
      {forJob && <p className="small muted" style={{ margin: '-6px 0 14px' }}>Job pages and their files are for Printees staff only.</p>}
      <label className="field"><span className="label">Staff password</span><input className={'in' + (err ? ' is-bad' : '')} type="password" autoComplete="current-password" value={pw} onChange={e => setPw(e.target.value)} /></label>
      {err && <p className="small" style={{ color: 'var(--printees-error-ink)', marginTop: 8 }}>{err}</p>}
      <button className="btn btn-primary btn-block" style={{ marginTop: 16 }}>Sign in</button>
    </form>
  );
}

function num(v: string) { const n = parseFloat(v); return isFinite(n) ? n : 0; }

function Settings({ onOut }: { onOut: () => void }) {
  const [b, setB] = useState<BuilderSettings | null>(null);
  const [p, setP] = useState<PricingConfig | null>(null);
  const [json, setJson] = useState('');
  const [subs, setSubs] = useState<any[]>([]);
  const [store, setStore] = useState<StorageReport | null>(null);
  const [saving, setSaving] = useState(false);
  const [t, setT] = useState<'dtf' | 'uv'>('dtf');
  useEffect(() => {
    api.adminSettings().then(r => { setB(r.builder); setP(r.pricing); setJson(JSON.stringify({ groups: r.pricing.groups, discountCodes: r.pricing.discountCodes }, null, 2)); });
    api.adminSubmissions().then(r => setSubs(r.submissions)).catch(() => {});
    api.adminStorage().then(setStore).catch(() => {});
  }, []);
  if (!b || !p) return null;
  const tp = p.transfers[t];
  const setTp = (patch: Partial<typeof tp>) => setP({ ...p, transfers: { ...p.transfers, [t]: { ...tp, ...patch } } });
  const setBl = (patch: Partial<typeof tp.byLength>) => setTp({ byLength: { ...tp.byLength, ...patch } });
  const save = async () => {
    setSaving(true);
    try {
      const extra = JSON.parse(json || '{}');
      const r = await api.adminSave({ builder: b, pricing: { ...p, groups: extra.groups || {}, discountCodes: extra.discountCodes || [] } });
      setB(r.builder); setP(r.pricing);
      toast('Saved. Customers see the new settings straight away.');
    } catch (e) { toast((e as Error).message, { bad: true, ms: 8000 }); }
    finally { setSaving(false); }
  };
  const F = (label: string, value: number, on: (v: number) => void, unit = '', step = 1) => (
    <label className="field"><span className="label">{label}</span><span className="unit" data-unit={unit}><input className="in num" type="number" step={step} value={value} onChange={e => on(num(e.target.value))} /></span></label>
  );
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 'auto' }}><span className="kicker">Staff</span><h1 className="h1" style={{ fontSize: 32 }}>Builder settings</h1></div>
        <button className="btn btn-line btn-sm" onClick={async () => { await api.adminLogout(); onOut(); }}>Sign out</button>
        <button className="btn btn-primary" disabled={saving} onClick={save}><Icon name="check" />{saving ? 'Saving…' : 'Save settings'}</button>
      </div>

      <section className="card" style={{ padding: 20, marginTop: 20 }}>
        <h2 className="h3" style={{ marginBottom: 12 }}>Sheet, spacing and quality</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {F('Printer max width', b.sheetWidthMm, v => setB({ ...b, sheetWidthMm: v }), 'mm')}
          {F('Edge margin', b.edgeMm, v => setB({ ...b, edgeMm: v }), 'mm', 0.5)}
          {F('Default gap', b.gapMm, v => setB({ ...b, gapMm: v }), 'mm', 0.5)}
          {F('Smallest gap customers may set', b.minGapMm, v => setB({ ...b, minGapMm: v }), 'mm', 0.5)}
          {F('Print resolution', b.dpi, v => setB({ ...b, dpi: Math.round(v) }), 'DPI')}
          {F('GOOD from', b.dpiGood, v => setB({ ...b, dpiGood: v }), 'DPI')}
          {F('FAIR from', b.dpiFair, v => setB({ ...b, dpiFair: v }), 'DPI')}
          {F('Block orders below (0 = never)', b.blockBelowDpi || 0, v => setB({ ...b, blockBelowDpi: v > 0 ? v : null }), 'DPI')}
          {F('Max upload size', b.upload.maxFileMb, v => setB({ ...b, upload: { ...b.upload, maxFileMb: v } }), 'MB')}
          {F('Max designs per gangsheet', b.upload.maxFilesPerProject, v => setB({ ...b, upload: { ...b.upload, maxFilesPerProject: Math.round(v) } }))}
        </div>
        <div style={{ marginTop: 14 }}><Switch checked={b.allowCustomerGap} onChange={v => setB({ ...b, allowCustomerGap: v })}>Customers may change the gap</Switch></div>
      </section>

      <section className="card" style={{ padding: 20, marginTop: 16 }}>
        <h2 className="h3" style={{ marginBottom: 4 }}>Spacing between printed artwork</h2>
        <p className="small muted" style={{ marginBottom: 12, maxWidth: 720 }}>The smallest gap between two designs, for each transfer type. Checked live in the builder and again on the server when a customer orders; Auto nest always keeps at least this much. <b>Bounding box</b> (the rule used before 0.3c) measures each design’s whole rectangle. <b>Artwork shape</b> measures the printed artwork, so transparent corners may overlap as long as the ink keeps the gap. Switching takes effect straight away for orders and new visits; a customer with the builder already open gets it when the page reloads.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, alignItems: 'end' }}>
          {F('Minimum gap · DTF', b.inkGapMm.dtf, v => setB({ ...b, inkGapMm: { ...b.inkGapMm, dtf: v } }), 'mm', 0.5)}
          {F('Minimum gap · UV DTF', b.inkGapMm.uv, v => setB({ ...b, inkGapMm: { ...b.inkGapMm, uv: v } }), 'mm', 0.5)}
          <div className="field"><span className="label">Check spacing by</span>
            <Seg<'artwork' | 'box'> label="Check spacing by" value={b.spacingCheck} onChange={v => setB({ ...b, spacingCheck: v })} options={[{ value: 'box', label: 'Bounding box' }, { value: 'artwork', label: 'Artwork shape' }]} />
          </div>
        </div>
      </section>

      <section className="card" style={{ padding: 20, marginTop: 16 }}>
        <h2 className="h3" style={{ marginBottom: 6 }}>Size presets</h2>
        <p className="small muted" style={{ marginBottom: 12 }}>Picking a preset sets the width; tall artwork is scaled to fit the max height.</p>
        <div style={{ display: 'grid', gap: 8 }}>
          {b.sizePresets.map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <label className="field"><span className="label">Name</span><input className="in" value={s.name} onChange={e => { const a = [...b.sizePresets]; a[i] = { ...s, name: e.target.value, key: s.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') }; setB({ ...b, sizePresets: a }); }} /></label>
              {F('Width', s.widthMm, v => { const a = [...b.sizePresets]; a[i] = { ...s, widthMm: v }; setB({ ...b, sizePresets: a }); }, 'mm')}
              {F('Max height', s.maxHeightMm || 0, v => { const a = [...b.sizePresets]; a[i] = { ...s, maxHeightMm: v > 0 ? v : undefined }; setB({ ...b, sizePresets: a }); }, 'mm')}
              <button className="ibtn is-danger" aria-label={'Remove ' + s.name} onClick={() => setB({ ...b, sizePresets: b.sizePresets.filter((_, j) => j !== i) })}><Icon name="trash" /></button>
            </div>
          ))}
        </div>
        <button className="btn btn-line btn-xs" style={{ marginTop: 10 }} onClick={() => setB({ ...b, sizePresets: [...b.sizePresets, { key: 'preset-' + Date.now().toString(36), name: 'New size', widthMm: 100 }] })}><Icon name="plus" />Add a preset</button>
      </section>

      <section className="card" style={{ padding: 20, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h2 className="h3" style={{ marginRight: 'auto' }}>Pricing ({p.currency}, {p.taxInclusive ? 'incl.' : 'excl.'} {p.taxLabel})</h2>
          <div className="seg"><button aria-pressed={t === 'dtf'} onClick={() => setT('dtf')}>DTF</button><button aria-pressed={t === 'uv'} onClick={() => setT('uv')}>UV DTF</button></div>
        </div>
        <h3 className="label" style={{ marginBottom: 8 }}>By the metre</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {F('Rate', tp.byLength.rate, v => setBl({ rate: v }), '$', 0.01)}
          <label className="field"><span className="label">Per</span><select className="in" value={tp.byLength.rateUnit} onChange={e => setBl({ rateUnit: e.target.value as any })}>{RATE_UNITS.map(u => <option key={u} value={u}>{u === 'm' ? 'metre' : u === 'cm' ? 'centimetre' : u === 'mm' ? 'millimetre' : u === 'in' ? 'inch' : 'foot'}</option>)}</select></label>
          {F('Sheet width', tp.byLength.widthMm, v => setBl({ widthMm: v }), 'mm')}
          {F('Minimum length', tp.byLength.minLengthMm, v => setBl({ minLengthMm: v }), 'mm')}
          {F('Longest sheet', tp.byLength.maxLengthMm, v => setBl({ maxLengthMm: v }), 'mm')}
          <label className="field"><span className="label">Bill the length</span><select className="in" value={tp.byLength.billingStepMm} onChange={e => setBl({ billingStepMm: num(e.target.value) })}>
            <option value={0}>Exactly</option><option value={10}>Up to the next 10 mm</option><option value={100}>Up to the next 10 cm</option><option value={500}>Up to the next half metre</option><option value={1000}>Up to the next metre</option>
          </select></label>
        </div>
        <h3 className="label" style={{ margin: '16px 0 8px' }}>Fixed sheets</h3>
        <div style={{ display: 'grid', gap: 8 }}>
          {tp.fixedSheets.map((f, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <label className="field"><span className="label">Name</span><input className="in" value={f.name} onChange={e => { const a = [...tp.fixedSheets]; a[i] = { ...f, name: e.target.value }; setTp({ fixedSheets: a }); }} /></label>
              {F('Width', f.widthMm, v => { const a = [...tp.fixedSheets]; a[i] = { ...f, widthMm: v }; setTp({ fixedSheets: a }); }, 'mm')}
              {F('Length', f.lengthMm, v => { const a = [...tp.fixedSheets]; a[i] = { ...f, lengthMm: v }; setTp({ fixedSheets: a }); }, 'mm')}
              {F('Price', f.price, v => { const a = [...tp.fixedSheets]; a[i] = { ...f, price: v }; setTp({ fixedSheets: a }); }, '$', 0.01)}
              <button className="ibtn is-danger" aria-label={'Remove ' + f.name} onClick={() => setTp({ fixedSheets: tp.fixedSheets.filter((_, j) => j !== i) })}><Icon name="trash" /></button>
            </div>
          ))}
        </div>
        <button className="btn btn-line btn-xs" style={{ marginTop: 10 }} onClick={() => setTp({ fixedSheets: [...tp.fixedSheets, { key: 'sheet-' + Date.now().toString(36), name: 'New sheet', widthMm: 580, lengthMm: 1000, price: 50 }] })}><Icon name="plus" />Add a fixed sheet</button>
        <h3 className="label" style={{ margin: '16px 0 8px' }}>Bulk tiers (total metres in the order)</h3>
        <div style={{ display: 'grid', gap: 8 }}>
          {tp.tiers.map((x, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
              {F('From', x.fromMetres, v => { const a = [...tp.tiers]; a[i] = { ...x, fromMetres: v }; setTp({ tiers: a }); }, 'm', 0.1)}
              {F('Discount', Math.round(x.percentOff * 1000) / 10, v => { const a = [...tp.tiers]; a[i] = { ...x, percentOff: v / 100 }; setTp({ tiers: a }); }, '%', 1)}
              <Switch checked={!!x.exclusive} onChange={v => { const a = [...tp.tiers]; a[i] = { ...x, exclusive: v }; setTp({ tiers: a }); }}>Only over</Switch>
              <button className="ibtn is-danger" aria-label="Remove tier" onClick={() => setTp({ tiers: tp.tiers.filter((_, j) => j !== i) })}><Icon name="trash" /></button>
            </div>
          ))}
        </div>
        <button className="btn btn-line btn-xs" style={{ marginTop: 10 }} onClick={() => setTp({ tiers: [...tp.tiers, { fromMetres: 100, percentOff: 0.55 }] })}><Icon name="plus" />Add a tier</button>
        <h3 className="label" style={{ margin: '16px 0 8px' }}>Rush</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
          <Switch checked={p.rush.enabled} onChange={v => setP({ ...p, rush: { ...p.rush, enabled: v } })}>Offer Rush</Switch>
          {F('Rush extra', Math.round(p.rush.percent * 100), v => setP({ ...p, rush: { ...p.rush, percent: v / 100 } }), '%')}
          {F('Rush up to', p.rush.maxMetres, v => setP({ ...p, rush: { ...p.rush, maxMetres: v } }), 'm')}
        </div>
        <h3 className="label" style={{ margin: '16px 0 8px' }}>Customer groups and discount codes (JSON)</h3>
        <textarea className="in" style={{ height: 180, padding: 12, fontFamily: 'var(--printees-mono)', fontSize: 12.5 }} value={json} onChange={e => setJson(e.target.value)} spellCheck={false} />
        <p className="tiny muted" style={{ marginTop: 6 }}>Groups: {'{ "wholesale": { "label": "Wholesale", "percentOff": 0.15 } }'}. Codes: {'[{ "code": "KURA10", "percentOff": 0.1, "active": true }]'}.</p>
      </section>

      {store && <StorageCard r={store} />}

      <EmailAdmin />

      <section className="card" style={{ padding: 20, marginTop: 16 }}>
        <h2 className="h3" style={{ marginBottom: 12 }}>Recent orders from the builder</h2>
        {!subs.length ? <p className="small muted">None yet.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Created', 'Gangsheet', 'Type', 'Sheets', 'Price', 'File', 'Upload page', ''].map(h => <th key={h} className="label" style={{ textAlign: 'left', padding: '6px 8px' }}>{h}</th>)}</tr></thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.id} style={{ borderTop: '1px solid var(--printees-border)' }}>
                    <td style={{ padding: '8px' }}>{new Date(s.createdAt).toLocaleString('en-NZ')}</td>
                    <td style={{ padding: '8px' }}>{s.projectName}</td>
                    <td style={{ padding: '8px' }}>{s.transfer === 'uv' ? 'UV DTF' : 'DTF'}</td>
                    <td style={{ padding: '8px' }}>{s.sheets.map((x: any) => Math.round(x.widthMm / 10) + '×' + Math.round(x.lengthMm / 10)).join(', ')} cm</td>
                    <td style={{ padding: '8px' }}>{formatMoney(s.price.total)}</td>
                    <td style={{ padding: '8px' }}>{s.status}</td>
                    <td style={{ padding: '8px' }}>{s.deliveryStatus === 'done' ? 'Sent · ' + (s.orderRef || '') : s.deliveryStatus || 'Not sent yet'}</td>
                    <td style={{ padding: '8px' }}><a className="link" href={s.staffUrl} target="_blank" rel="noreferrer">Job page</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

const gb = (b: number) => b < 1073741824 ? Math.max(0, Math.round(b / 1048576)) + ' MB' : (b / 1073741824).toFixed(b >= 10 * 1073741824 ? 0 : 1) + ' GB';

/** Disk space and upload activity, with a warning before uploads would stop. */
function StorageCard({ r }: { r: StorageReport }) {
  return (
    <section className="card" style={{ padding: 20, marginTop: 16 }}>
      <h2 className="h3" style={{ marginBottom: 12 }}>Storage</h2>
      {r.warning && <div className="callout callout-warn" style={{ marginBottom: 12 }}><Icon name="alert" />{r.warning}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {r.disks.map(d => (
          <div key={d.label}>
            <span className="label">{d.label}</span>
            <b style={{ fontSize: 18 }}>{gb(d.free)} free</b>
            <div className="small muted">of {gb(d.total)}</div>
            <div className="bar" style={{ marginTop: 6 }}><i style={{ width: Math.round((1 - d.free / Math.max(1, d.total)) * 100) + '%', background: d.free < r.minFreeGb * 4 * 1073741824 ? 'var(--printees-warning)' : undefined }} /></div>
          </div>
        ))}
        <div><span className="label">Uploaded artwork</span><b style={{ fontSize: 18 }}>{gb(r.assets.bytes)}</b><div className="small muted">{r.assets.count} files{r.driver === 's3' ? ' · in R2' : ''}</div></div>
        <div><span className="label">Last 24 hours</span><b style={{ fontSize: 18 }}>{r.assets.last24h} uploads</b><div className="small muted">from {r.assets.visitors24h} visitors · {r.orders} orders in total</div></div>
      </div>
      <p className="tiny muted" style={{ marginTop: 10 }}>Uploads pause when less than {r.minFreeGb} GB is free. Each visitor can keep up to {r.visitorQuotaGb} GB. Limits are set in the server’s .env (see docs/SECURITY.md).</p>
    </section>
  );
}
